import * as React from 'react'
  /* @jsx mdx */
import { mdx } from '@mdx-js/react';
/* @jsxRuntime classic */

/* @jsx mdx */

export const _frontmatter = {
  "title": "Add granular role-based access to your JAMStack app",
  "description": "",
  "published": "2020-05-18T00:00:00.000Z",
  "redirect_from": ["/blog/add-granular-role-based-access-to-your-jamstack-app/swizec/9418"],
  "categories": "Front End, Technical",
  "hero": "./img/techletter.app-screenshot-1589818032252.png"
};
const layoutProps = {
  _frontmatter
};
const MDXLayout = "wrapper";
export default function MDXContent({
  components,
  ...props
}) {
  return <MDXLayout {...layoutProps} {...props} components={components} mdxType="MDXLayout">
    <p>{`Say you've got a JAMStack app with authentication. Works great, loads fast, some pages need login.`}</p>
    <p>{`That part's easy with something like `}<a parentName="p" {...{
        "href": "https://github.com/Swizec/useAuth"
      }}>{`useAuth`}</a>{`, a manual integration with Auth0, or any number of 3rd party providers.`}</p>
    <p>{`You have some code that asks `}<em parentName="p">{`"Is this user logged in?"`}</em>{`. Show them the page or some "Please Login" interface. There's a button somewhere that starts the login flow.`}</p>
    <p>{`Something like this for example:`}</p>
    <p><img parentName="p" {...{
        "src": "/d96d35d3c8bed79f2af50efe6e96ca61/HREDYcq.gif",
        "alt": null
      }}></img></p>
    <p>{`Now what if you want to add an area that only some users have access to? How would you do that?`}</p>
    <p><img parentName="p" {...{
        "src": "/f7da90ff549b239f93f00f81ea19e851/5eSMcCw.gif",
        "alt": null
      }}></img></p>
    <p>{`🤔`}</p>
    <h2 {...{
      "id": "roles-are-the-answer"
    }}>{`Roles are the answer`}</h2>
    <p>{`User roles are the simplest approach to granular permissions. Everything else I've tried gets out of hand super fast.`}</p>
    <p>{`Is this user a student or not? Access to course.`}</p>
    <p>{`Does this user have module X? Access.`}</p>
    <p>{`You can go as detailed as you want. Admin vs. not-admin is often the first and only role-based permission. Some apps eventually need more.`}</p>
    <p>{`The more roles you add, the more complex it all gets. But trust me, roles are the only approach that scales at all.`}</p>
    <p>{`A friend of mine lived through a horror story where it took an entire team and 3 years to build a robust permission system for a large app. 3 years 😳`}</p>
    <p>{`Roles are way easier.`}</p>
    <h2 {...{
      "id": "with-useauth-070"
    }}>{`With useAuth 0.7.0`}</h2>
    <p><a parentName="p" {...{
        "href": "https://www.youtube.com/watch?v=X9PHqmnzr4k"
      }}><figure parentName="a" {...{
          "className": "gatsby-resp-image-figure",
          "style": {
            "textAlign": "center",
            "fontStyle": "italic"
          }
        }}>{`
    `}<span parentName="figure" {...{
            "className": "gatsby-resp-image-wrapper",
            "style": {
              "position": "relative",
              "display": "block",
              "marginLeft": "auto",
              "marginRight": "auto",
              "maxWidth": "640px"
            }
          }}>{`
      `}<span parentName="span" {...{
              "className": "gatsby-resp-image-background-image",
              "style": {
                "paddingBottom": "56.05381165919282%",
                "position": "relative",
                "bottom": "0",
                "left": "0",
                "backgroundImage": "url('data:image/svg+xml,%3csvg%20xmlns=\\'http://www.w3.org/2000/svg\\'%20width=\\'400\\'%20height=\\'225\\'%20viewBox=\\'0%200%20400%20225\\'%20preserveAspectRatio=\\'none\\'%3e%3cpath%20d=\\'M0%202l84%201a213183%20213183%200%2001109-2L96%200C9%200%200%200%200%202m277-1c-2%201-8%201%2061%202l62-1c0-2-6-2-61-2l-62%201M60%2093l1%2029h99l1-29%201-27-51-1H60v28m130%2013v12c1%202%2011%202%2016%201l4-1v-12l-10-1-10%201M74%20159l-37%201a460%20460%200%2000-11%201h25l-4%202c-4%203-5%203-5%201s-2-2-21-2H0v63h401v-32c0-30%200-30-1-6v27H297l-74%201v-28l-1-27a16000%2016000%200%2000-143-1c-2-2-5-2-5%200m3%207l1%205%201%204v5l-1-4-3-3-2%201-1%206v10l-1%201v2l1%201c1-1%208-4%2012-4l14-1c9-1%2010-2%2012-4l2-3v4l1-10v-15H76l1%205\\'%20fill=\\'%23d3d3d3\\'%20fill-rule=\\'evenodd\\'/%3e%3c/svg%3e')",
                "backgroundSize": "cover",
                "display": "block"
              }
            }}></span>{`
  `}<picture parentName="span">{`
          `}<source parentName="picture" {...{
                "srcSet": ["/static/58dffb094b41031d5c84fe0a43a85465/ca0a1/techletter.app-screenshot-1589818032252.webp 223w", "/static/58dffb094b41031d5c84fe0a43a85465/75680/techletter.app-screenshot-1589818032252.webp 445w", "/static/58dffb094b41031d5c84fe0a43a85465/0ba47/techletter.app-screenshot-1589818032252.webp 640w"],
                "sizes": "(max-width: 640px) 100vw, 640px",
                "type": "image/webp"
              }}></source>{`
          `}<source parentName="picture" {...{
                "srcSet": ["/static/58dffb094b41031d5c84fe0a43a85465/e92b6/techletter.app-screenshot-1589818032252.png 223w", "/static/58dffb094b41031d5c84fe0a43a85465/e66bf/techletter.app-screenshot-1589818032252.png 445w", "/static/58dffb094b41031d5c84fe0a43a85465/6af66/techletter.app-screenshot-1589818032252.png 640w"],
                "sizes": "(max-width: 640px) 100vw, 640px",
                "type": "image/png"
              }}></source>{`
          `}<img parentName="picture" {...{
                "className": "gatsby-resp-image-image",
                "src": "/static/58dffb094b41031d5c84fe0a43a85465/6af66/techletter.app-screenshot-1589818032252.png",
                "alt": "Click through for source",
                "title": "Click through for source",
                "loading": "lazy",
                "style": {
                  "width": "100%",
                  "height": "100%",
                  "margin": "0",
                  "verticalAlign": "middle",
                  "position": "absolute",
                  "top": "0",
                  "left": "0"
                }
              }}></img>{`
        `}</picture>{`
    `}</span>{`
    `}<figcaption parentName="figure" {...{
            "className": "gatsby-resp-image-figcaption"
          }}>{`Click through for source`}</figcaption>{`
  `}</figure></a></p>
    <p>{`Hot off the presses, `}<a parentName="p" {...{
        "href": "https://github.com/Swizec/useAuth"
      }}>{`useAuth 0.7.0`}</a>{` adds a helper to check for user roles. Still just for Auth0, soon for others I promise.`}</p>
    <p>{`Here's what you do:`}</p>
    <p><a parentName="p" {...{
        "href": "https://carbon.now.sh/?bg=rgba(255,255,255,1)&t=seti&l=javascript&ds=true&wc=true&wa=true&pv=48px&ph=32px&ln=false&code=const%20ForStudentsOnly%20%3D%20()%20%3D%3E%20%7B%0A%09const%20%7B%20isAuthorized%20%7D%20%3D%20useAuth()%0A%09%0A%09if%20(isAuthorized(%22Student%22))%20%7B%0A%09%09return%20%3CReallyCoolContent%20%2F%3E%0A%09%7D%20else%20%7B%0A%09%09return%20%3CNoAccess%20%2F%3E%0A%09%7D%0A%7D"
      }}><figure parentName="a" {...{
          "className": "gatsby-resp-image-figure",
          "style": {
            "textAlign": "center",
            "fontStyle": "italic"
          }
        }}>{`
    `}<span parentName="figure" {...{
            "className": "gatsby-resp-image-wrapper",
            "style": {
              "position": "relative",
              "display": "block",
              "marginLeft": "auto",
              "marginRight": "auto",
              "maxWidth": "406px"
            }
          }}>{`
      `}<span parentName="span" {...{
              "className": "gatsby-resp-image-background-image",
              "style": {
                "paddingBottom": "81.61434977578475%",
                "position": "relative",
                "bottom": "0",
                "left": "0",
                "backgroundImage": "url('data:image/svg+xml,%3csvg%20xmlns=\\'http://www.w3.org/2000/svg\\'%20width=\\'400\\'%20height=\\'326\\'%20viewBox=\\'0%200%20400%20326\\'%20preserveAspectRatio=\\'none\\'%3e%3cpath%20d=\\'M33%2048c-3%203-3%20228%200%20231%203%202%20331%202%20334%200%203-3%203-228%200-231-3-2-331-2-334%200m35%2016c-4%204-1%2010%204%2010%202%200%206-4%206-6%200-3-4-6-6-6l-4%202\\'%20fill=\\'%23d3d3d3\\'%20fill-rule=\\'evenodd\\'/%3e%3c/svg%3e')",
                "backgroundSize": "cover",
                "display": "block"
              }
            }}></span>{`
  `}<picture parentName="span">{`
          `}<source parentName="picture" {...{
                "srcSet": ["/static/222bf6973ea8bee7d25cb0a71e66edcd/ca0a1/techletter.app-screenshot-1589818031718.webp 223w", "/static/222bf6973ea8bee7d25cb0a71e66edcd/09a6f/techletter.app-screenshot-1589818031718.webp 406w"],
                "sizes": "(max-width: 406px) 100vw, 406px",
                "type": "image/webp"
              }}></source>{`
          `}<source parentName="picture" {...{
                "srcSet": ["/static/222bf6973ea8bee7d25cb0a71e66edcd/e92b6/techletter.app-screenshot-1589818031718.png 223w", "/static/222bf6973ea8bee7d25cb0a71e66edcd/e33ef/techletter.app-screenshot-1589818031718.png 406w"],
                "sizes": "(max-width: 406px) 100vw, 406px",
                "type": "image/png"
              }}></source>{`
          `}<img parentName="picture" {...{
                "className": "gatsby-resp-image-image",
                "src": "/static/222bf6973ea8bee7d25cb0a71e66edcd/e33ef/techletter.app-screenshot-1589818031718.png",
                "alt": "Click through for source",
                "title": "Click through for source",
                "loading": "lazy",
                "style": {
                  "width": "100%",
                  "height": "100%",
                  "margin": "0",
                  "verticalAlign": "middle",
                  "position": "absolute",
                  "top": "0",
                  "left": "0"
                }
              }}></img>{`
        `}</picture>{`
    `}</span>{`
    `}<figcaption parentName="figure" {...{
            "className": "gatsby-resp-image-figcaption"
          }}>{`Click through for source`}</figcaption>{`
  `}</figure></a></p>
    <p>{`And that's pretty much it, really.`}</p>
    <p>{`The `}<inlineCode parentName="p">{`isAuthorized`}</inlineCode>{` method verifies your user is currently logged-in and that they have the `}<inlineCode parentName="p">{`Student`}</inlineCode>{` role.`}</p>
    <p>{`You'll need to add a rule to your Auth0 config as well. They don't send this info by default. Don't know why, I tried everything. 😔`}</p>
    <p><a parentName="p" {...{
        "href": "https://carbon.now.sh/?bg=rgba(255,255,255,1)&t=seti&l=javascript&ds=true&wc=true&wa=true&pv=48px&ph=32px&ln=false&code=%2F%2F%20this%20goes%20in%20Auth0%2Frules%20config%20as%20a%20new%20rule%0Afunction%20(user%2C%20context%2C%20callback)%20%7B%0A%20%20const%20namespace%20%3D%20'https%3A%2F%2FYOUR_DOMAIN'%3B%0A%20%20const%20assignedRoles%20%3D%20(context.authorization%20%7C%7C%20%7B%7D).roles%3B%0A%20%20%0A%20%20user.user_metadata%20%3D%20user.user_metadata%20%7C%7C%20%7B%7D%3B%0A%0A%20%20user.user_metadata.roles%20%3D%20assignedRoles%3B%0A%0A%20%20context.idToken%5Bnamespace%20%2B%20'%2Fuser_metadata'%5D%20%3D%20user.user_metadata%3B%0A%0A%20%20callback(null%2C%20user%2C%20context)%3B%0A%7D"
      }}><figure parentName="a" {...{
          "className": "gatsby-resp-image-figure",
          "style": {
            "textAlign": "center",
            "fontStyle": "italic"
          }
        }}>{`
    `}<span parentName="figure" {...{
            "className": "gatsby-resp-image-wrapper",
            "style": {
              "position": "relative",
              "display": "block",
              "marginLeft": "auto",
              "marginRight": "auto",
              "maxWidth": "654px"
            }
          }}>{`
      `}<span parentName="span" {...{
              "className": "gatsby-resp-image-background-image",
              "style": {
                "paddingBottom": "61.43497757847533%",
                "position": "relative",
                "bottom": "0",
                "left": "0",
                "backgroundImage": "url('data:image/svg+xml,%3csvg%20xmlns=\\'http://www.w3.org/2000/svg\\'%20width=\\'400\\'%20height=\\'246\\'%20viewBox=\\'0%200%20400%20246\\'%20preserveAspectRatio=\\'none\\'%3e%3cpath%20d=\\'M20%2030a2762%202762%200%20000%20186c3%203%20357%203%20360%200l1-93-1-93c-1-2-359-2-360%200\\'%20fill=\\'%23d3d3d3\\'%20fill-rule=\\'evenodd\\'/%3e%3c/svg%3e')",
                "backgroundSize": "cover",
                "display": "block"
              }
            }}></span>{`
  `}<picture parentName="span">{`
          `}<source parentName="picture" {...{
                "srcSet": ["/static/2c21e6bba743311a373e42c926efa2c8/ca0a1/techletter.app-screenshot-1589818029535.webp 223w", "/static/2c21e6bba743311a373e42c926efa2c8/75680/techletter.app-screenshot-1589818029535.webp 445w", "/static/2c21e6bba743311a373e42c926efa2c8/d7085/techletter.app-screenshot-1589818029535.webp 654w"],
                "sizes": "(max-width: 654px) 100vw, 654px",
                "type": "image/webp"
              }}></source>{`
          `}<source parentName="picture" {...{
                "srcSet": ["/static/2c21e6bba743311a373e42c926efa2c8/e92b6/techletter.app-screenshot-1589818029535.png 223w", "/static/2c21e6bba743311a373e42c926efa2c8/e66bf/techletter.app-screenshot-1589818029535.png 445w", "/static/2c21e6bba743311a373e42c926efa2c8/68e9c/techletter.app-screenshot-1589818029535.png 654w"],
                "sizes": "(max-width: 654px) 100vw, 654px",
                "type": "image/png"
              }}></source>{`
          `}<img parentName="picture" {...{
                "className": "gatsby-resp-image-image",
                "src": "/static/2c21e6bba743311a373e42c926efa2c8/68e9c/techletter.app-screenshot-1589818029535.png",
                "alt": "Click through for source",
                "title": "Click through for source",
                "loading": "lazy",
                "style": {
                  "width": "100%",
                  "height": "100%",
                  "margin": "0",
                  "verticalAlign": "middle",
                  "position": "absolute",
                  "top": "0",
                  "left": "0"
                }
              }}></img>{`
        `}</picture>{`
    `}</span>{`
    `}<figcaption parentName="figure" {...{
            "className": "gatsby-resp-image-figcaption"
          }}>{`Click through for source`}</figcaption>{`
  `}</figure></a></p>
    <p>{`And you have to make sure that namespace matches a config in the `}<inlineCode parentName="p">{`<AuthProvider>`}</inlineCode>{` that wraps your whole component tree. That's how `}<inlineCode parentName="p">{`useAuth`}</inlineCode>{` hooks into the React context and keeps track of everything.`}</p>
    <p><a parentName="p" {...{
        "href": "https://carbon.now.sh/?bg=rgba(255,255,255,1)&t=seti&l=javascript&ds=true&wc=true&wa=true&pv=48px&ph=32px&ln=false&code=export%20const%20wrapPageElement%20%3D%20(%7B%20element%2C%20props%20%7D)%20%3D%3E%20(%0A%20%20%3CAuthProvider%0A%20%20%20%20%2F%2F%20...%0A%20%20%20%20customPropertyNamespace%3D%22https%3A%2F%2FYOUR_DOMAIN%22%0A%20%20%3E%0A%20%20%20%20%3CLayout%20%7B...props%7D%3E%7Belement%7D%3C%2FLayout%3E%0A%20%20%3C%2FAuthProvider%3E%0A)"
      }}><figure parentName="a" {...{
          "className": "gatsby-resp-image-figure",
          "style": {
            "textAlign": "center",
            "fontStyle": "italic"
          }
        }}>{`
    `}<span parentName="figure" {...{
            "className": "gatsby-resp-image-wrapper",
            "style": {
              "position": "relative",
              "display": "block",
              "marginLeft": "auto",
              "marginRight": "auto",
              "maxWidth": "550px"
            }
          }}>{`
      `}<span parentName="span" {...{
              "className": "gatsby-resp-image-background-image",
              "style": {
                "paddingBottom": "56.95067264573991%",
                "position": "relative",
                "bottom": "0",
                "left": "0",
                "backgroundImage": "url('data:image/svg+xml,%3csvg%20xmlns=\\'http://www.w3.org/2000/svg\\'%20width=\\'400\\'%20height=\\'228\\'%20viewBox=\\'0%200%20400%20228\\'%20preserveAspectRatio=\\'none\\'%3e%3cpath%20d=\\'M24%2037c-2%203-1%20154%201%20155%202%203%20348%203%20350%200%202-1%203-152%201-155S26%2034%2024%2037\\'%20fill=\\'%23d3d3d3\\'%20fill-rule=\\'evenodd\\'/%3e%3c/svg%3e')",
                "backgroundSize": "cover",
                "display": "block"
              }
            }}></span>{`
  `}<picture parentName="span">{`
          `}<source parentName="picture" {...{
                "srcSet": ["/static/a1160ca703fbbdcd11cd71b8b68b8c80/ca0a1/techletter.app-screenshot-1589818029965.webp 223w", "/static/a1160ca703fbbdcd11cd71b8b68b8c80/75680/techletter.app-screenshot-1589818029965.webp 445w", "/static/a1160ca703fbbdcd11cd71b8b68b8c80/12b65/techletter.app-screenshot-1589818029965.webp 550w"],
                "sizes": "(max-width: 550px) 100vw, 550px",
                "type": "image/webp"
              }}></source>{`
          `}<source parentName="picture" {...{
                "srcSet": ["/static/a1160ca703fbbdcd11cd71b8b68b8c80/e92b6/techletter.app-screenshot-1589818029965.png 223w", "/static/a1160ca703fbbdcd11cd71b8b68b8c80/e66bf/techletter.app-screenshot-1589818029965.png 445w", "/static/a1160ca703fbbdcd11cd71b8b68b8c80/dd45a/techletter.app-screenshot-1589818029965.png 550w"],
                "sizes": "(max-width: 550px) 100vw, 550px",
                "type": "image/png"
              }}></source>{`
          `}<img parentName="picture" {...{
                "className": "gatsby-resp-image-image",
                "src": "/static/a1160ca703fbbdcd11cd71b8b68b8c80/dd45a/techletter.app-screenshot-1589818029965.png",
                "alt": "Click through for source",
                "title": "Click through for source",
                "loading": "lazy",
                "style": {
                  "width": "100%",
                  "height": "100%",
                  "margin": "0",
                  "verticalAlign": "middle",
                  "position": "absolute",
                  "top": "0",
                  "left": "0"
                }
              }}></img>{`
        `}</picture>{`
    `}</span>{`
    `}<figcaption parentName="figure" {...{
            "className": "gatsby-resp-image-figcaption"
          }}>{`Click through for source`}</figcaption>{`
  `}</figure></a></p>
    <p>{`No, I don't know why the namespace needs to be a full URL. The Auth0 documentation isn't clear on that part and I was unable to hunt down details on the forums.`}</p>
    <h2 {...{
      "id": "how-do-you-get-roles-onto-users-in-the-first-place"
    }}>{`How do you get roles onto users in the first place?`}</h2>
    <p>{`Ah yes, `}<em parentName="p">{`adding`}</em>{` roles to users. That part is a little tricky.`}</p>
    <p>{`Here's an article I wrote on `}<a parentName="p" {...{
        "href": "https://swizec.com/blog/connecting-gumroad-to-auth0-for-paywalled-jamstack-apps/swizec/9336"
      }}>{`Connecting Gumroad to Auth0 for paywalled JAMStack apps`}</a>{` ❤️`}</p>
    <p>{`The TL;DR is that your checkout provider triggers your cloud function through a webhook and that adds a role to your user. You can also do it manually.`}</p>
    <p>{`I use Gumroad, Stripe works too. I use AWS Lambda, a Netlify or Vercel cloud function should be fine.`}</p>
    <p><a parentName="p" {...{
        "href": "https://carbon.now.sh/?bg=rgba(255,255,255,1)&t=seti&l=javascript&ds=true&wc=true&wa=true&pv=48px&ph=32px&ln=false&code=%2F%2F%20this%20bit%20of%20code%20adds%20a%20role%20to%20user%0A%20%20if%20(FindYourIdea_PRODUCTS.includes(ping.product_permalink))%20%7B%0A%20%20%20%20%2F%2F%20create%20or%20get%20user%0A%20%20%20%20const%20user%20%3D%20await%20upsertUser(ping)%0A%0A%20%20%20%20if%20(user)%20%7B%0A%20%20%20%20%20%20const%20auth0%20%3D%20await%20getAuth0Client()%0A%0A%20%20%20%20%20%20%2F%2F%20add%20the%20right%20role%0A%20%20%20%20%20%20await%20auth0.assignRolestoUser(%0A%20%20%20%20%20%20%20%20%7B%20id%3A%20user.user_id!%20%7D%2C%0A%20%20%20%20%20%20%20%20%7B%0A%20%20%20%20%20%20%20%20%20%20%2F%2F%20hardcoded%20FindYourIdea%20role%20id%20from%20Auth0%20URL%0A%20%20%20%20%20%20%20%20%20%20roles%3A%20%5BROLE_ID%5D%2C%0A%20%20%20%20%20%20%20%20%7D%0A%20%20%20%20%20%20)%0A%20%20%20%20%7D%0A%20%20%7D"
      }}><figure parentName="a" {...{
          "className": "gatsby-resp-image-figure",
          "style": {
            "textAlign": "center",
            "fontStyle": "italic"
          }
        }}>{`
    `}<span parentName="figure" {...{
            "className": "gatsby-resp-image-wrapper",
            "style": {
              "position": "relative",
              "display": "block",
              "marginLeft": "auto",
              "marginRight": "auto",
              "maxWidth": "606px"
            }
          }}>{`
      `}<span parentName="span" {...{
              "className": "gatsby-resp-image-background-image",
              "style": {
                "paddingBottom": "81.1659192825112%",
                "position": "relative",
                "bottom": "0",
                "left": "0",
                "backgroundImage": "url('data:image/svg+xml,%3csvg%20xmlns=\\'http://www.w3.org/2000/svg\\'%20width=\\'400\\'%20height=\\'325\\'%20viewBox=\\'0%200%20400%20325\\'%20preserveAspectRatio=\\'none\\'%3e%3cpath%20d=\\'M33%2032l-11%201-1%20129%201%20131c1%202%20355%202%20356%200l1-131V33l-12-1H33\\'%20fill=\\'%23d3d3d3\\'%20fill-rule=\\'evenodd\\'/%3e%3c/svg%3e')",
                "backgroundSize": "cover",
                "display": "block"
              }
            }}></span>{`
  `}<picture parentName="span">{`
          `}<source parentName="picture" {...{
                "srcSet": ["/static/b3a692a04479b4ed03ab741fccce5892/ca0a1/techletter.app-screenshot-1589818029816.webp 223w", "/static/b3a692a04479b4ed03ab741fccce5892/75680/techletter.app-screenshot-1589818029816.webp 445w", "/static/b3a692a04479b4ed03ab741fccce5892/18e7e/techletter.app-screenshot-1589818029816.webp 606w"],
                "sizes": "(max-width: 606px) 100vw, 606px",
                "type": "image/webp"
              }}></source>{`
          `}<source parentName="picture" {...{
                "srcSet": ["/static/b3a692a04479b4ed03ab741fccce5892/e92b6/techletter.app-screenshot-1589818029816.png 223w", "/static/b3a692a04479b4ed03ab741fccce5892/e66bf/techletter.app-screenshot-1589818029816.png 445w", "/static/b3a692a04479b4ed03ab741fccce5892/4d4a2/techletter.app-screenshot-1589818029816.png 606w"],
                "sizes": "(max-width: 606px) 100vw, 606px",
                "type": "image/png"
              }}></source>{`
          `}<img parentName="picture" {...{
                "className": "gatsby-resp-image-image",
                "src": "/static/b3a692a04479b4ed03ab741fccce5892/4d4a2/techletter.app-screenshot-1589818029816.png",
                "alt": "Click through for source",
                "title": "Click through for source",
                "loading": "lazy",
                "style": {
                  "width": "100%",
                  "height": "100%",
                  "margin": "0",
                  "verticalAlign": "middle",
                  "position": "absolute",
                  "top": "0",
                  "left": "0"
                }
              }}></img>{`
        `}</picture>{`
    `}</span>{`
    `}<figcaption parentName="figure" {...{
            "className": "gatsby-resp-image-figcaption"
          }}>{`Click through for source`}</figcaption>{`
  `}</figure></a></p>
    <p>{`Full details in the `}<a parentName="p" {...{
        "href": "https://swizec.com/blog/connecting-gumroad-to-auth0-for-paywalled-jamstack-apps/swizec/9336"
      }}>{`Connecting Gumroad to Auth0 for paywalled JAMStack apps`}</a>{` article.`}</p>
    <h2 {...{
      "id": "what-if-theres-many-roles-to-check"
    }}>{`What if there's many roles to check?`}</h2>
    <p>{`This is where life gets tricky. The more roles you have, the trickier. 😅`}</p>
    <p>{`Somewhere in your code there's going to be a component like this.`}</p>
    <p><a parentName="p" {...{
        "href": "https://carbon.now.sh/?bg=rgba(255,255,255,1)&t=seti&l=javascript&ds=true&wc=true&wa=true&pv=48px&ph=32px&ln=false&code=const%20LockedContent%20%3D%20(props)%20%3D%3E%20%7B%0A%20%20const%20allowUnauth%20%3D%20UNAUTH_PAGES.includes(currentLocation(props))%0A%20%20const%20%7B%20isAuthorized%2C%20isAuthenticated%20%7D%20%3D%20useAuth()%0A%0A%20%20if%20(!allowUnauth%20%26%26%20!isAuthenticated())%20%7B%0A%20%20%20%20%2F%2F%20not%20even%20logged%20in%2C%20ask%20for%20login%0A%20%20%20%20return%20%3CPleaseLogin%20%2F%3E%0A%20%20%7D%20else%20%7B%0A%20%20%20%20if%20(isFindYourIdeaPage(props))%20%7B%0A%20%20%20%20%20%20if%20(isAuthorized(%22FindYourIdea%22))%20%7B%0A%20%20%20%20%20%20%20%20%2F%2F%20FindYourIdea%20page%2C%20access%20-%3E%20show%0A%20%20%20%20%20%20%20%20return%20%3CContent%20%7B...props%7D%20%2F%3E%0A%20%20%20%20%20%20%7D%20else%20%7B%0A%20%20%20%20%20%20%20%20%2F%2F%20FindYourIdea%20page%2C%20no%20access%20-%3E%20buy%0A%20%20%20%20%20%20%20%20return%20%3CPleasePurchase%20findYourIdea%20%2F%3E%0A%20%20%20%20%20%20%7D%0A%20%20%20%20%7D%20else%20if%20(isAuthorized(%22Student%22))%20%7B%0A%20%20%20%20%20%20%2F%2F%20ServerlessReactDev%20page%2C%20acces%20-%3E%20show%0A%20%20%20%20%20%20return%20%3CContent%20%7B...props%7D%20%2F%3E%0A%20%20%20%20%7D%20else%20if%20(allowUnauth)%20%7B%0A%20%20%20%20%20%20%2F%2F%20no%20auth%20required%20-%3E%20show%0A%20%20%20%20%20%20return%20%3CContent%20%7B...props%7D%20%2F%3E%0A%20%20%20%20%7D%20else%20%7B%0A%20%20%20%20%20%20%2F%2F%20ServerlessReactDev%20page%2C%20no%20access%20-%3E%20buy%0A%20%20%20%20%20%20return%20%3CPleasePurchase%20%2F%3E%0A%20%20%20%20%7D%0A%20%20%7D%0A%7D"
      }}><figure parentName="a" {...{
          "className": "gatsby-resp-image-figure",
          "style": {
            "textAlign": "center",
            "fontStyle": "italic"
          }
        }}>{`
    `}<span parentName="figure" {...{
            "className": "gatsby-resp-image-wrapper",
            "style": {
              "position": "relative",
              "display": "block",
              "marginLeft": "auto",
              "marginRight": "auto",
              "maxWidth": "638px"
            }
          }}>{`
      `}<span parentName="span" {...{
              "className": "gatsby-resp-image-background-image",
              "style": {
                "paddingBottom": "105.38116591928251%",
                "position": "relative",
                "bottom": "0",
                "left": "0",
                "backgroundImage": "url('data:image/svg+xml,%3csvg%20xmlns=\\'http://www.w3.org/2000/svg\\'%20width=\\'400\\'%20height=\\'422\\'%20viewBox=\\'0%200%20400%20422\\'%20preserveAspectRatio=\\'none\\'%3e%3cpath%20d=\\'M21%2031l-1%20181c0%20176%200%20179%202%20180h356c2-1%202-4%202-180l-1-181c-2-2-356-2-358%200\\'%20fill=\\'%23d3d3d3\\'%20fill-rule=\\'evenodd\\'/%3e%3c/svg%3e')",
                "backgroundSize": "cover",
                "display": "block"
              }
            }}></span>{`
  `}<picture parentName="span">{`
          `}<source parentName="picture" {...{
                "srcSet": ["/static/23df3f2017cbd5aec8d701fa204e441c/ca0a1/techletter.app-screenshot-1589818029955.webp 223w", "/static/23df3f2017cbd5aec8d701fa204e441c/75680/techletter.app-screenshot-1589818029955.webp 445w", "/static/23df3f2017cbd5aec8d701fa204e441c/a2d8a/techletter.app-screenshot-1589818029955.webp 638w"],
                "sizes": "(max-width: 638px) 100vw, 638px",
                "type": "image/webp"
              }}></source>{`
          `}<source parentName="picture" {...{
                "srcSet": ["/static/23df3f2017cbd5aec8d701fa204e441c/e92b6/techletter.app-screenshot-1589818029955.png 223w", "/static/23df3f2017cbd5aec8d701fa204e441c/e66bf/techletter.app-screenshot-1589818029955.png 445w", "/static/23df3f2017cbd5aec8d701fa204e441c/41be6/techletter.app-screenshot-1589818029955.png 638w"],
                "sizes": "(max-width: 638px) 100vw, 638px",
                "type": "image/png"
              }}></source>{`
          `}<img parentName="picture" {...{
                "className": "gatsby-resp-image-image",
                "src": "/static/23df3f2017cbd5aec8d701fa204e441c/41be6/techletter.app-screenshot-1589818029955.png",
                "alt": "Click through for source",
                "title": "Click through for source",
                "loading": "lazy",
                "style": {
                  "width": "100%",
                  "height": "100%",
                  "margin": "0",
                  "verticalAlign": "middle",
                  "position": "absolute",
                  "top": "0",
                  "left": "0"
                }
              }}></img>{`
        `}</picture>{`
    `}</span>{`
    `}<figcaption parentName="figure" {...{
            "className": "gatsby-resp-image-figcaption"
          }}>{`Click through for source`}</figcaption>{`
  `}</figure></a></p>
    <p>{`Like I said, messy. And there's nothing you can do about it.`}</p>
    <p>{`Might look a little better, if you bake it into your router and use individual checks on individual pages. But the complexity remains. Somewhere something has to check this stuff. 🤷‍♀️`}</p>
    <p>{`Using different components for different content pages makes it easier – check authorization in the component itself. But I'm using some MDX shenanigans and the `}<inlineCode parentName="p">{`<Content>`}</inlineCode>{` component doesn't know what it's showing.`}</p>
    <p>{`So a nice big truth table is what I gotta do.`}</p>
    <p><a parentName="p" {...{
        "href": "https://twitter.com/Swizec/status/1250144894808547328"
      }}><figure parentName="a" {...{
          "className": "gatsby-resp-image-figure",
          "style": {
            "textAlign": "center",
            "fontStyle": "italic"
          }
        }}>{`
    `}<span parentName="figure" {...{
            "className": "gatsby-resp-image-wrapper",
            "style": {
              "position": "relative",
              "display": "block",
              "marginLeft": "auto",
              "marginRight": "auto",
              "maxWidth": "640px"
            }
          }}>{`
      `}<span parentName="span" {...{
              "className": "gatsby-resp-image-background-image",
              "style": {
                "paddingBottom": "129.59641255605382%",
                "position": "relative",
                "bottom": "0",
                "left": "0",
                "backgroundImage": "url('data:image/svg+xml,%3csvg%20xmlns=\\'http://www.w3.org/2000/svg\\'%20width=\\'400\\'%20height=\\'518\\'%20viewBox=\\'0%200%20400%20518\\'%20preserveAspectRatio=\\'none\\'%3e%3cpath%20d=\\'M23%2017c-7%204-10%2011-6%2019s14%2011%2021%206l3-2-3-1c-5-2-3-6%203-6%203%200%203%200%203-4%200-10-12-17-21-12m39%20267v10l-7%201-23%201-15%201h12a232%20232%200%200032-1v12l-1%2025-1%2013%201-12%202-25%201-14h24l36%202h12v3c0%202%200%202%201%200l1-3h29l23-1-29-1h-23v-3l-1-3v3c0%203%200%203-6%203h-18l-31-1H63v-10l-1-9v9\\'%20fill=\\'%23d3d3d3\\'%20fill-rule=\\'evenodd\\'/%3e%3c/svg%3e')",
                "backgroundSize": "cover",
                "display": "block"
              }
            }}></span>{`
  `}<picture parentName="span">{`
          `}<source parentName="picture" {...{
                "srcSet": ["/static/62a469cda2f33b595e25a343ad0fa816/ca0a1/techletter.app-screenshot-1589818030521.webp 223w", "/static/62a469cda2f33b595e25a343ad0fa816/75680/techletter.app-screenshot-1589818030521.webp 445w", "/static/62a469cda2f33b595e25a343ad0fa816/0ba47/techletter.app-screenshot-1589818030521.webp 640w"],
                "sizes": "(max-width: 640px) 100vw, 640px",
                "type": "image/webp"
              }}></source>{`
          `}<source parentName="picture" {...{
                "srcSet": ["/static/62a469cda2f33b595e25a343ad0fa816/e92b6/techletter.app-screenshot-1589818030521.png 223w", "/static/62a469cda2f33b595e25a343ad0fa816/e66bf/techletter.app-screenshot-1589818030521.png 445w", "/static/62a469cda2f33b595e25a343ad0fa816/6af66/techletter.app-screenshot-1589818030521.png 640w"],
                "sizes": "(max-width: 640px) 100vw, 640px",
                "type": "image/png"
              }}></source>{`
          `}<img parentName="picture" {...{
                "className": "gatsby-resp-image-image",
                "src": "/static/62a469cda2f33b595e25a343ad0fa816/6af66/techletter.app-screenshot-1589818030521.png",
                "alt": "Click through for source",
                "title": "Click through for source",
                "loading": "lazy",
                "style": {
                  "width": "100%",
                  "height": "100%",
                  "margin": "0",
                  "verticalAlign": "middle",
                  "position": "absolute",
                  "top": "0",
                  "left": "0"
                }
              }}></img>{`
        `}</picture>{`
    `}</span>{`
    `}<figcaption parentName="figure" {...{
            "className": "gatsby-resp-image-figcaption"
          }}>{`Click through for source`}</figcaption>{`
  `}</figure></a></p>
    <h2 {...{
      "id": "what-about-without-useauth"
    }}>{`What about without useAuth?`}</h2>
    <p>{`Same principle my friend. You get the role for your user and you ask `}<em parentName="p">{`"Does this role have access to this page?"`}</em></p>
    <p>{`✌️`}</p>
    <p>{`Cheers,`}<br parentName="p"></br>{`
`}{`~Swizec`}</p>
    <p>{`PS: the `}<a parentName="p" {...{
        "href": "https://swizec.com/blog/looking-for-the-perfect-light-work-device/swizec/9413"
      }}>{`coding-on-a-ipad workflow I suggested on Friday totally worked`}</a>{` 🤘`}</p>
    <p><a parentName="p" {...{
        "href": "https://youtu.be/OqZeMqRqr9A"
      }}><figure parentName="a" {...{
          "className": "gatsby-resp-image-figure",
          "style": {
            "textAlign": "center",
            "fontStyle": "italic"
          }
        }}>{`
    `}<span parentName="figure" {...{
            "className": "gatsby-resp-image-wrapper",
            "style": {
              "position": "relative",
              "display": "block",
              "marginLeft": "auto",
              "marginRight": "auto",
              "maxWidth": "640px"
            }
          }}>{`
      `}<span parentName="span" {...{
              "className": "gatsby-resp-image-background-image",
              "style": {
                "paddingBottom": "56.05381165919282%",
                "position": "relative",
                "bottom": "0",
                "left": "0",
                "backgroundImage": "url('data:image/svg+xml,%3csvg%20xmlns=\\'http://www.w3.org/2000/svg\\'%20width=\\'400\\'%20height=\\'225\\'%20viewBox=\\'0%200%20400%20225\\'%20preserveAspectRatio=\\'none\\'%3e%3cpath%20d=\\'M71%201L31%2014c-6%200-21%2014-26%2024l-4%206-1%2013v12h2l5%202c3%204%204%202%202-3-3-5-2-6%205-6l12-3c13-2%2016-2%2016%200l2-1c5-5%2011-7%2016-5%203%201%203%201%203-1%200-3%204-3%2011%200%206%204%206%204%208%202%201-2%201-3-1-2l-2-1-2-3c-2-2%200-3%2014-7%2019-4%2047-2%2061%205%2014%208%2023%2031%2019%2048-3%209-9%2019-12%2022l-6%206-1%204-1-4c-1-5-4-5-7-1l-4%205-2%202-3-3-2-4-2-3c-2-3-4-3-8-2-3%202-5-4-4-13%201-15-5-27-19-37l-9-7c-1-2-3-3-6-3-4%200-4%200-3%203%200%203-1%205-5%2010-12%2012-15%2025-9%2037%206%2011%2019%2017%2029%2014l5-1h-7c-12%200-20-4-25-14-6-10-3-21%206-31%2013-12%2027-9%2036%208%208%2015%205%2029-8%2035l-3%202%203-1c13-2%2017%201%2012%2011l-3%206c0%202%202%201%203-2%201-2%203-2%203%201l3%202%202%203-2%206c-1%208-3%2011-9%2014-7%204-9%203-9-4%200-4-1-5-3-5l-9-4-8-4v3c1%201%200%203-2%204l-6%207-2%206-5-4-4-7c0-2-1-3-5-6l-11-8-6-5c-1%201%205%2011%207%2012%201%200%206%206%206%208l2%202v13c-1%205-6%204-12-1-6-7-14-8-15-2%200%202%201%203%204%206%203%202%203%203%204%209l1%207v1c-1%201-11%200-12-1l-3-1-5-2-5-5-3-6-1-2-2-2-2-3-4-10-2-9c-1-1-1%207-1%2018v20l2-1c2%200%203%200%204%202%204%206%2012%2013%2016%2014%205%202%2016%201%2017-1l4-3%205-3%205-2c8-1%2017-3%2020-5l4-1-20%2016-15%2011h-6l-10-1c-4-2-11-3-13-3l-3-1-4-1-4-3c-2-1-2%200-2%2011v12h401v-41c0-22%200-40-1-39%200%202%201%202-24%200l-21-1%2010%202a423%20423%200%200133%203c2%200%202%200%202%208v8l-11-1a718%20718%200%2001-22%201c1%201-1%202-2%200l-3-1c-2%201-6%200-6-2h2l1-1c-2-2-5-1-6%202-1%201-1%202-2%201l-3-1-2-2-2-1-2-1-1-1c-3%200-2%203%201%203%202%201%202%201-1%201-2%200-3-1-3-2v-4l1-2v-7l1-3h6c5%200%206%200%204-1l-7-1h-3l1-14%202-32c3-18%203-22%200-25-2-1-3-4-1-5l1-4%201-6c2-5%201-14%200-10l-1%205c-1%204-2%203-3-2%200-4%200-5%202-7l3-3c0-2%2010-6%2011-5%202%202%202%2022-1%2038l1%204c1%201%204-20%206-33l-1-7c-1%200-2-1-1-3%200-3%202-3%206-4%205%200%205%201%208-15l2-14h-14c-11%200-13%200-13%202l-2%201h-2l2%202a615%20615%200%2000-12%2045l-5%2012-1%206h-6l-51-5c-94-7-88-6-88-10l2-3a1893%201893%200%200190%205l1-4c2-2%202-4%201-6-3-6-2-7%208-8l1-1-2-1-1-1-4-1-4%201h-1l-1-3c0-2%200-2%201-1h1c0-3-2-5-3-3s-2-2-1-7l-1-3c-2%200-3-3-1-3%202-2%200-6-3-6-2%200-3-2-3-4l2-1c7-3-2-3-57-3h-60v6l-1%2015-9-5A68%2068%200%200090%200L71%201m227%201c-1%203%201%207%203%208l16%201%2014%201v-2c2-7%202-7-13-7l-17-2-3%201m7%2015c-2%204-1%205%202%205%202%201%203%200%201-1h7l5%202%205%201c3%200%204%200%204-3l1-4-13-1-12%201m-2%2013c0%203%202%205%2010%206%2010%201%2013%200%2014-4v-3h-6c-12-2-17-1-18%201m62%206l-2%206-1%2015v14l1%206-2%205c0%205%200%205%201%204%202%200%202%200%202%205v10c1%206%201%209-1%209l-1-3v-4l-1%203c0%205-1%206-4%206l-3%201-3%202c-4%201-5%203-3%203l8-4h3l-2%203c-3%202-2%203%202%202%203%200%204-3%202-3v-2c0-2%200-2%201%200%201%201%201%201%201-1s2-4%202-2v4c-1%202-1%203%201%203l1-5%201-2c2%201%203-3%201-4v-2l1-8%202-7%202-6%202-11%202-9%202-10c1-7%201-13-1-11l-1%203-1%203-1-5c0-5%200-6-2-7s-2-1-2%201%200%203-1%202v-2c0-2%200-2-2-2h-4m-14%201l-1%209c0%208%200%208-1%206l-2-3v11c2%200%202%201%201%205l-1%2011-3%2023-1%2019c1%200%202-1%201-3l1-3c1%200%203-6%202-7s-1-2%201-3c1-1%202-4%204-17%204-34%204-50-1-48m-44%207c-1%204-1%205%208%205%208%201%209%200%2010-5v-3h-6c-11-2-12-1-12%203m-2%207v4c0%204%201%205%203%205%205%202%207%201%208-2%202-3%202-4%204-4%204%200%204-2%201-2a161%20161%200%2001-16-1M183%2082l35%205%208%201-2%201h-2c1%202%2011%203%2011%201l-3-1h-3l2-1c3%200%202-2-1-2a160%20160%200%2000-22-3%20146%20146%200%2000-20-2l-3%201m208%2044l-10%203-3%202h3c4%200%2014%203%2015%204%202%202%204%200%204-6s0-6-9-3m-328%209l-1%204c2%208%2010%2012%2015%208%203-2%204-5%201-5l-2-2-2-2-3-2h-4c-1%201-2%200-3-1h-1m293%2062h-3l-5%201-10%203-6%201-1%205v6l6%202%206%204c0%202%200%203%202%203l3-1h14l1-1%201%201c1%202%207%203%208%201%200-2%201-2%203-1%204%202%208%201%208-1h1l2%201h8c0-1%206%200%206%202%201%201%201-4%201-11s0-11-1-10c0%204%201%204-14%200-33-7-30-6-30-5m-224%208l-9%202-9%201c-6%200-28%206-23%206l-4%203-4%202%204%201c5%202%2042%200%2050-2%2012-2%2015-7%2010-12-3-2-5-2-15-1M3%20213l33%201c27%200%2030%200%2018-1H3\\'%20fill=\\'%23d3d3d3\\'%20fill-rule=\\'evenodd\\'/%3e%3c/svg%3e')",
                "backgroundSize": "cover",
                "display": "block"
              }
            }}></span>{`
  `}<picture parentName="span">{`
          `}<source parentName="picture" {...{
                "srcSet": ["/static/ad7d713a0d6a708b591c162b3d4a7417/ca0a1/techletter.app-screenshot-1589818031419.webp 223w", "/static/ad7d713a0d6a708b591c162b3d4a7417/75680/techletter.app-screenshot-1589818031419.webp 445w", "/static/ad7d713a0d6a708b591c162b3d4a7417/0ba47/techletter.app-screenshot-1589818031419.webp 640w"],
                "sizes": "(max-width: 640px) 100vw, 640px",
                "type": "image/webp"
              }}></source>{`
          `}<source parentName="picture" {...{
                "srcSet": ["/static/ad7d713a0d6a708b591c162b3d4a7417/e92b6/techletter.app-screenshot-1589818031419.png 223w", "/static/ad7d713a0d6a708b591c162b3d4a7417/e66bf/techletter.app-screenshot-1589818031419.png 445w", "/static/ad7d713a0d6a708b591c162b3d4a7417/6af66/techletter.app-screenshot-1589818031419.png 640w"],
                "sizes": "(max-width: 640px) 100vw, 640px",
                "type": "image/png"
              }}></source>{`
          `}<img parentName="picture" {...{
                "className": "gatsby-resp-image-image",
                "src": "/static/ad7d713a0d6a708b591c162b3d4a7417/6af66/techletter.app-screenshot-1589818031419.png",
                "alt": "Click through for source",
                "title": "Click through for source",
                "loading": "lazy",
                "style": {
                  "width": "100%",
                  "height": "100%",
                  "margin": "0",
                  "verticalAlign": "middle",
                  "position": "absolute",
                  "top": "0",
                  "left": "0"
                }
              }}></img>{`
        `}</picture>{`
    `}</span>{`
    `}<figcaption parentName="figure" {...{
            "className": "gatsby-resp-image-figcaption"
          }}>{`Click through for source`}</figcaption>{`
  `}</figure></a></p>

    </MDXLayout>;
}
;
MDXContent.isMDXComponent = true;
      